import React, { useMemo } from 'react';
import useStyles from '../../theme/useStyles';
import { formatDate } from '../../utils/format';
import { peakOf, percent } from '../../utils/summaryStats';
import {
  KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, Badge
} from '../../components/ui';
import { choirSummary } from './analytics';
import type { ChoirMemberLike, ChoirScheduleLike, ChoirAssetLike } from './analytics';

export interface ChoirSummaryProps {
  members: ChoirMemberLike[];
  schedules: ChoirScheduleLike[];
  assets: ChoirAssetLike[];
  loading?: boolean;
  isDarkMode?: boolean;
}

/** The Choir module's Summary tab: tiles, then report panels and watch-lists. */
const ChoirSummaryView = ({ members, schedules, assets, loading, isDarkMode }: ChoirSummaryProps) => {
  const t = useStyles(isDarkMode).t;
  const s = useMemo(() => choirSummary(members, schedules, assets), [members, schedules, assets]);
  const voicePeak = peakOf(s.voices, (r) => r.count);
  const monthPeak = peakOf(s.months, (r) => r.count);
  const statusPeak = peakOf(s.assetsByStatus, (r) => r.count);

  return (
    <>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="Singing Members" value={String(s.singing)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="On Leave / Inactive" value={String(s.away.length)} color={s.away.length ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Voice Parts" value={String(s.voices.filter((v) => v.name !== 'Not set').length)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Joined This Year" value={String(s.newThisYear)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Next 30 Days" value={String(s.upcoming.length)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Practices (30 d)" value={String(s.practicesLast30)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Services (30 d)" value={String(s.servicesLast30)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Avg. Headcount" value={s.avgHeadcount === null ? '—' : String(s.avgHeadcount)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Library Items" value={String(assets.length)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Needs Attention" value={String(s.needsAttention.length)} color={s.needsAttention.length ? t.danger : t.income} isDarkMode={isDarkMode} />
      </KpiGrid>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Voice Balance"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Voice / Instrument', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.count, s.singing)} max={100} showPercent color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Singers', align: 'right' }
          ]}
          rows={s.voices}
          rowKey="name"
          emptyMessage="No singing members yet."
          below={s.voices.length ? <TotalRow label="Total singing" value={s.singing} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title="Roles"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Role', render: (r) => <strong>{r.name}</strong> },
            { key: 'count', header: 'Members', align: 'right' }
          ]}
          rows={s.roles}
          rowKey="name"
          emptyMessage="No roles recorded yet."
        />
      </SummaryColumns>

      <TablePanel
        title="Schedules by Month"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="420px"
        columns={[
          { key: 'label', header: 'Month', nowrap: true, render: (r) => <strong>{r.label}</strong> },
          { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={monthPeak} color={t.info} isDarkMode={isDarkMode} /> },
          { key: 'count', header: 'Schedules', align: 'right' }
        ]}
        rows={s.months}
        rowKey="month"
        emptyMessage="No schedules recorded yet."
      />

      <TablePanel
        title="Coming Up — Next 30 Days"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="520px"
        columns={[
          { key: 'date', header: 'Date', nowrap: true, render: (r) => <strong>{formatDate(r.date)}</strong> },
          { key: 'time', header: 'Time', nowrap: true, render: (r) => r.time || '-' },
          { key: 'eventType', header: 'Event', render: (r) => <Badge tone="neutral">{r.eventType || '-'}</Badge> },
          { key: 'conductor', header: 'Conductor', render: (r) => r.conductor || '-' },
          { key: 'songs', header: 'Songs', wrap: true, render: (r) => r.songs || '-' }
        ]}
        rows={s.upcoming}
        rowKey={(r) => `${r.date}|${r.time}|${r.eventType}`}
        emptyMessage="Nothing scheduled in the next 30 days."
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Library by Status"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Status', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={statusPeak} color={r.name === 'Available' ? t.income : r.name === 'Issued' ? t.warning : t.danger} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Items', align: 'right' }
          ]}
          rows={s.assetsByStatus}
          rowKey="name"
          emptyMessage="No library items yet."
          below={assets.length ? <TotalRow label="Total items" value={assets.length} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title="Library by Category"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Category', render: (r) => <strong>{r.name}</strong> },
            { key: 'count', header: 'Items', align: 'right' }
          ]}
          rows={s.assetsByCategory}
          rowKey="name"
          emptyMessage="No library items yet."
        />
      </SummaryColumns>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Issued to Members"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'title', header: 'Item', render: (r) => <strong>{r.title || '-'}</strong> },
            { key: 'assignedTo', header: 'With', render: (r) => r.assignedTo || '-' }
          ]}
          rows={s.issued}
          rowKey={(r) => `${r.title}|${r.assignedTo}`}
          emptyMessage="Nothing is out on loan."
        />
        <TablePanel
          title="Members Away"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Member', render: (r) => <strong>{r.name || '-'}</strong> },
            { key: 'voiceType', header: 'Voice', render: (r) => r.voiceType || '-' },
            { key: 'memberStatus', header: 'Status', render: (r) => <Badge tone="warning">{r.memberStatus}</Badge> }
          ]}
          rows={s.away}
          rowKey={(r) => `${r.name}|${r.voiceType}`}
          emptyMessage="Every member is singing."
        />
      </SummaryColumns>

      {s.needsAttention.length > 0 && (
        <TablePanel
          title="Library Items Needing Attention"
          isDarkMode={isDarkMode}
          columns={[
            { key: 'title', header: 'Item', render: (r) => <strong>{r.title || '-'}</strong> },
            { key: 'category', header: 'Category', render: (r) => r.category || '-' },
            { key: 'assetStatus', header: 'Status', render: (r) => <Badge tone="error">{r.assetStatus}</Badge> }
          ]}
          rows={s.needsAttention}
          rowKey={(r) => `${r.title}|${r.category}`}
        />
      )}
    </>
  );
};

export default ChoirSummaryView;
